/**
 * ProfilePhotoRequests — admin queue of member-submitted "set as profile
 * photo" requests with one-tap approve / reject.
 */
import { useCallback, useEffect, useState } from "react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Loader2 } from "lucide-react";

export default function ProfilePhotoRequests({ treeId, onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!treeId) return;
    http.get(`/family-trees/${treeId}/profile-photo-requests`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, [treeId]);
  useEffect(load, [load]);

  const decide = async (cid, action) => {
    setBusy(cid);
    try {
      await http.post(`/family-trees/${treeId}/profile-photo-requests/${cid}/${action}`);
      toast.success(action === "approve" ? "Profile photo updated!" : "Request dismissed.");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    } finally {
      setBusy(null);
    }
  };

  if (!items.length) return null;
  return (
    <div className="rounded-xl border-2 border-dashed p-3 mb-4" style={{ borderColor: "#F59E0B", background: "#FFFBEB" }} data-testid="profile-photo-requests">
      <div className="text-xs font-semibold mb-2" style={{ color: "#92400E" }}>
        {items.length} profile-photo request{items.length === 1 ? "" : "s"} awaiting your approval
      </div>
      <div className="flex flex-wrap gap-3">
        {items.map((c) => (
          <div key={c.id} className="rounded-xl border bg-white p-2 w-40" style={{ borderColor: "var(--wofy-border)" }} data-testid={`photo-request-${c.id}`}>
            <img src={resolveImage(c.url)} alt="" className="w-full h-28 object-cover rounded-lg" loading="lazy" />
            <div className="text-[11px] mt-1.5 truncate">
              For <b>{c.member?.name || "member"}</b>
            </div>
            <div className="text-[10px] opacity-60 truncate">by {c.uploader_name || c.uploader_email || "a member"}</div>
            <div className="flex gap-1.5 mt-2">
              <button
                type="button"
                disabled={busy === c.id}
                onClick={() => decide(c.id, "approve")}
                className="flex-1 rounded-full py-1 text-[11px] font-semibold text-white inline-flex items-center justify-center gap-1"
                style={{ background: "#16A34A" }}
                data-testid={`photo-request-approve-${c.id}`}
              >
                {busy === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
              </button>
              <button
                type="button"
                disabled={busy === c.id}
                onClick={() => decide(c.id, "reject")}
                className="rounded-full px-2.5 py-1 text-[11px] font-semibold border"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`photo-request-reject-${c.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
